import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Search } from "react-feather";
import { programService } from "@/services/program.service";
import { useDebounce } from "@/hooks/useDebounce";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DataTablePagination } from "@/components/ui/data-table";
import {
  UnderlineTabs,
  type UnderlineTabItem,
} from "@/components/ui/underline-tabs";
import { cn } from "@/lib/utils";
import {
  AffiliateRosterTable,
  type ProgramAffiliateRow,
} from "./AffiliateRosterTable";
import { RemoveAffiliatesModal } from "./RemoveAffiliatesModal";

const PAGE_SIZE_OPTIONS = [20, 50, 100, 500] as const;

// Large selections are split into sequential requests so no single call can
// outlive a gateway timeout; progress is surfaced through one updating toast.
const BULK_ASSIGN_CHUNK_SIZE = 20;
const BULK_ASSIGN_TOAST_ID = "bulk-assign-progress";

type BulkAssignResult = {
  affiliateId: string;
  success: boolean;
  error?: string;
};

type Tab = "assigned" | "available" | "applicants";

type AffiliateManagementCardProps = {
  programId: string;
  isSuspended?: boolean;
  showAssignPanel?: boolean;
  /** Auto-approving programs never accumulate applicants. */
  automaticApproval?: boolean;
  attributionMethod?: "STATIC_CODE" | "SNEAKYLINK";
  className?: string;
};

export function AffiliateManagementCard({
  programId,
  isSuspended,
  showAssignPanel = true,
  automaticApproval,
  attributionMethod,
  className,
}: AffiliateManagementCardProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>("assigned");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [pageSize, setPageSize] = useState<number>(20);
  const [assignedPage, setAssignedPage] = useState(1);
  const [availablePage, setAvailablePage] = useState(1);
  const [applicantsPage, setApplicantsPage] = useState(1);
  const [adding, setAdding] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ProgramAffiliateRow | null>(
    null,
  );
  const [selectedAssignedIds, setSelectedAssignedIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedAvailableIds, setSelectedAvailableIds] = useState<Set<string>>(
    new Set(),
  );
  // Keyed on APPLICATION ids, not affiliate ids — see ProgramAffiliateRow.id.
  const [selectedApplicantIds, setSelectedApplicantIds] = useState<Set<string>>(
    new Set(),
  );
  const [bulkRemoveConfirmOpen, setBulkRemoveConfirmOpen] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  useEffect(() => {
    setAssignedPage(1);
    setAvailablePage(1);
    setApplicantsPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!showAssignPanel && activeTab === "available") {
      setActiveTab("assigned");
    }
  }, [showAssignPanel, activeTab]);

  const {
    data: assignedData,
    isLoading: isLoadingAssigned,
    isFetching: isFetchingAssigned,
  } = useQuery({
    queryKey: [
      "program-users",
      programId,
      assignedPage,
      pageSize,
      debouncedSearch,
    ],
    queryFn: async () => {
      return await programService.listUsersInProgram(
        programId,
        assignedPage,
        pageSize,
        debouncedSearch || undefined,
      );
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: true,
  });

  const {
    data: availableData,
    isLoading: isLoadingAvailable,
    isFetching: isFetchingAvailable,
  } = useQuery({
    queryKey: [
      "available-affiliates",
      programId,
      availablePage,
      pageSize,
      debouncedSearch,
    ],
    queryFn: async () => {
      return await programService.getAvailableAffiliates(
        programId,
        availablePage,
        pageSize,
        debouncedSearch || undefined,
      );
    },
    enabled: showAssignPanel,
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: true,
  });

  const {
    data: applicantsData,
    isLoading: isLoadingApplicants,
    isFetching: isFetchingApplicants,
  } = useQuery({
    queryKey: [
      "program-applicants",
      programId,
      applicantsPage,
      pageSize,
      debouncedSearch,
    ],
    queryFn: async () => {
      return await programService.listApplicantsInProgram(
        programId,
        applicantsPage,
        pageSize,
        debouncedSearch || undefined,
      );
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: true,
  });

  const assignedUsers: ProgramAffiliateRow[] = assignedData?.data ?? [];
  const assignedPagination = assignedData?.pagination;
  const otherUsers: ProgramAffiliateRow[] = availableData?.data ?? [];
  const availablePagination = availableData?.pagination;

  // The roster table selects on `row.id`, so an applicant row carries its
  // APPLICATION id there and keeps the affiliate id alongside. One affiliate can
  // hold applications to several programs, and each is decided separately.
  const applicantRows: ProgramAffiliateRow[] = useMemo(
    () =>
      (applicantsData?.data ?? []).map((applicant) => ({
        id: applicant.applicationId,
        affiliateId: applicant.id,
        email: applicant.email,
        firstName: applicant.firstName,
        lastName: applicant.lastName,
        code: applicant.code ?? "",
        status: applicant.status,
      })),
    [applicantsData],
  );
  const applicantsPagination = applicantsData?.pagination;
  const applicantsTotal = applicantsPagination?.total ?? 0;

  // An auto-approving program can't accumulate applicants, so the tab only
  // appears where it can mean something (or where history left some behind).
  const showApplicantsTab = !automaticApproval || applicantsTotal > 0;

  useEffect(() => {
    if (!showApplicantsTab && activeTab === "applicants") {
      setActiveTab("assigned");
    }
  }, [showApplicantsTab, activeTab]);

  const byTab = <T,>(assigned: T, available: T, applicants: T): T =>
    activeTab === "assigned"
      ? assigned
      : activeTab === "available"
        ? available
        : applicants;

  const currentPage = byTab(assignedPage, availablePage, applicantsPage);
  const setCurrentPage = byTab(
    setAssignedPage,
    setAvailablePage,
    setApplicantsPage,
  );
  const currentRows = byTab(assignedUsers, otherUsers, applicantRows);
  const currentPagination = byTab(
    assignedPagination,
    availablePagination,
    applicantsPagination,
  );
  const selectedIds = byTab(
    selectedAssignedIds,
    selectedAvailableIds,
    selectedApplicantIds,
  );
  const setSelectedIds = byTab(
    setSelectedAssignedIds,
    setSelectedAvailableIds,
    setSelectedApplicantIds,
  );
  const isLoading = byTab(
    isLoadingAssigned && !assignedData,
    isLoadingAvailable && !availableData,
    isLoadingApplicants && !applicantsData,
  );
  const isFetching = byTab(
    isFetchingAssigned,
    isFetchingAvailable,
    isFetchingApplicants,
  );

  const currentPageIds = currentRows.map((u) => u.id);
  const allPageSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedIds.has(id));
  const totalMatching = currentPagination?.total ?? 0;
  const allAcrossPagesSelected =
    totalMatching > 0 && selectedIds.size >= totalMatching;
  const showSelectAllAcrossPages =
    allPageSelected &&
    totalMatching > currentPageIds.length &&
    !allAcrossPagesSelected;

  const invalidateAffiliateQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["program-users", programId] }),
      queryClient.invalidateQueries({
        queryKey: ["available-affiliates", programId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["program-affiliate-count", programId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["program-affiliate-count"],
      }),
      // Approving an applicant turns it into an enrollment, so both lists move.
      queryClient.invalidateQueries({
        queryKey: ["program-applicants", programId],
      }),
    ]);
  };

  const decideApplicationsMutation = useMutation({
    mutationFn: async ({
      applicationIds,
      approve,
    }: {
      applicationIds: string[];
      approve: boolean;
    }) =>
      approve
        ? programService.approveApplications(applicationIds)
        : programService.rejectApplications(applicationIds),
    onSuccess: async (_result, variables) => {
      const count = variables.applicationIds.length;
      const noun = `applicant${count === 1 ? "" : "s"}`;
      setSelectedApplicantIds(new Set());
      toast.success(
        variables.approve
          ? `${count} ${noun} approved`
          : `${count} ${noun} rejected`,
      );
      await invalidateAffiliateQueries();
    },
    onError: (err: unknown) => {
      const message = (err as AxiosError<{ message: string | string[] }>)
        ?.response?.data?.message;
      if (Array.isArray(message)) {
        message.forEach((m) => toast.error(m));
      } else {
        toast.error(message || "Failed to update the applications");
      }
    },
  });

  // A removal the backend refused because a payout is still owed (a request
  // that landed after the dialog's preflight). Re-run the preflight so the
  // open dialog flips to its settle view instead of just toasting.
  const handleRemovalError = (err: unknown, fallback: string) => {
    const response = (err as AxiosError<{ code?: string; message?: string }>)
      ?.response;
    const body = response?.data;
    if (response?.status === 409 && body?.code === "PENDING_PAYOUT") {
      queryClient.invalidateQueries({
        queryKey: ["removal-preflight", programId],
      });
    }
    toast.error(body?.message || fallback);
  };

  const removeMutation = useMutation({
    mutationFn: (affiliateId: string) =>
      programService.removeAffiliateFromProgram(programId, affiliateId),
    onSuccess: async (_data, affiliateId) => {
      await invalidateAffiliateQueries();
      setSelectedAssignedIds((prev) => {
        if (!prev.has(affiliateId)) return prev;
        const next = new Set(prev);
        next.delete(affiliateId);
        return next;
      });
      setRemoveTarget(null);
      toast.success("Affiliate removed from program");
    },
    onError: (err: unknown) =>
      handleRemovalError(err, "Failed to remove affiliate from program"),
  });

  const bulkRemoveMutation = useMutation({
    mutationFn: (affiliateIds: string[]) =>
      programService.bulkRemoveAffiliatesFromProgram(programId, affiliateIds),
    onSuccess: async (data) => {
      await invalidateAffiliateQueries();
      setSelectedAssignedIds(new Set());
      setBulkRemoveConfirmOpen(false);
      const failed = data.results.filter(
        (r: { success: boolean }) => !r.success,
      ).length;
      const succeeded = data.results.length - failed;
      if (failed) {
        toast.error(`${succeeded} removed, ${failed} failed`);
      } else {
        toast.success(
          `${succeeded} affiliate${succeeded === 1 ? "" : "s"} removed`,
        );
      }
    },
    onError: (err: unknown) =>
      handleRemovalError(err, "Failed to remove affiliates from program"),
  });

  // The one removal dialog: a single row (trash icon) or the bulk selection.
  const bulkRemovalIds = useMemo(
    () => Array.from(selectedAssignedIds),
    [selectedAssignedIds],
  );
  const removalRequest = removeTarget
    ? {
        ids: [removeTarget.id],
        subject:
          `${removeTarget.firstName ?? ""} ${removeTarget.lastName ?? ""}`.trim() ||
          removeTarget.email,
      }
    : bulkRemoveConfirmOpen
      ? {
          ids: bulkRemovalIds,
          subject: `${bulkRemovalIds.length} affiliate${
            bulkRemovalIds.length === 1 ? "" : "s"
          }`,
        }
      : null;
  const isRemoving = removeMutation.isPending || bulkRemoveMutation.isPending;
  const closeRemovalDialog = () => {
    if (isRemoving) return;
    setRemoveTarget(null);
    setBulkRemoveConfirmOpen(false);
  };
  const confirmRemoval = (affiliateIds: string[]) => {
    if (affiliateIds.length === 0) return;
    if (removeTarget) {
      removeMutation.mutate(affiliateIds[0]);
    } else {
      bulkRemoveMutation.mutate(affiliateIds);
    }
  };

  const bulkAssignMutation = useMutation({
    mutationFn: async (affiliateIds: string[]) => {
      const results: BulkAssignResult[] = [];
      for (let i = 0; i < affiliateIds.length; i += BULK_ASSIGN_CHUNK_SIZE) {
        const chunk = affiliateIds.slice(i, i + BULK_ASSIGN_CHUNK_SIZE);
        if (affiliateIds.length > BULK_ASSIGN_CHUNK_SIZE) {
          toast.loading(
            `Assigning ${Math.min(i + chunk.length, affiliateIds.length)} of ${affiliateIds.length}…`,
            { id: BULK_ASSIGN_TOAST_ID },
          );
        }
        const res = await programService.bulkAddAffiliatesToProgram(
          programId,
          chunk,
        );
        results.push(...(res.results ?? []));
      }
      return { results };
    },
    onSuccess: async (data) => {
      setSelectedAvailableIds(new Set());
      setAvailablePage(1);
      const failed = data.results.filter((r) => !r.success).length;
      const succeeded = data.results.length - failed;
      if (failed) {
        toast.error(`${succeeded} assigned, ${failed} failed`, {
          id: BULK_ASSIGN_TOAST_ID,
        });
      } else {
        toast.success(
          `${succeeded} affiliate${succeeded === 1 ? "" : "s"} assigned`,
          { id: BULK_ASSIGN_TOAST_ID },
        );
      }
      // Awaited so isPending keeps the buttons disabled until both lists
      // reflect the new assignments (stale rows would allow double-assigns).
      await invalidateAffiliateQueries();
    },
    onError: async (err: unknown) => {
      const errorMessage =
        (err as AxiosError<{ message: string }>)?.response?.data?.message ||
        "Failed to assign affiliates";
      toast.error(errorMessage, { id: BULK_ASSIGN_TOAST_ID });
      // Earlier chunks may already have landed — refresh so both lists match
      // what the server actually has.
      await invalidateAffiliateQueries();
    },
  });

  const handleAdd = async (affiliateId: string) => {
    if (isSuspended) {
      toast.error(
        "Cannot assign affiliates to a suspended program. Please upgrade your plan.",
      );
      return;
    }
    try {
      setAdding(affiliateId);
      await programService.addAffiliateToProgram(programId, affiliateId);
      toast.success("Affiliate assigned successfully");
      setSelectedAvailableIds((prev) => {
        if (!prev.has(affiliateId)) return prev;
        const next = new Set(prev);
        next.delete(affiliateId);
        return next;
      });
      if (otherUsers.length === 1 && availablePage > 1) {
        setAvailablePage(1);
      }
      await invalidateAffiliateQueries();
    } catch {
      toast.error("Failed to assign affiliate");
    } finally {
      setAdding(null);
    }
  };

  const toggleId = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        currentPageIds.forEach((id) => next.delete(id));
      } else {
        currentPageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleSelectAllAcrossPages = async () => {
    if (!currentPagination?.total) return;
    try {
      setIsSelectingAll(true);
      if (activeTab === "applicants") {
        const res = await programService.listApplicantsInProgram(
          programId,
          1,
          currentPagination.total,
          debouncedSearch || undefined,
        );
        // Application ids, matching how applicant rows are keyed.
        setSelectedApplicantIds(
          new Set((res.data || []).map((a) => a.applicationId)),
        );
      } else if (activeTab === "assigned") {
        const res = await programService.listUsersInProgram(
          programId,
          1,
          currentPagination.total,
          debouncedSearch || undefined,
        );
        const ids = (res.data || []).map((u: { id: string }) => u.id);
        setSelectedAssignedIds(new Set(ids));
      } else {
        const res = await programService.getAvailableAffiliates(
          programId,
          1,
          currentPagination.total,
          debouncedSearch || undefined,
        );
        const ids = (res.data || []).map((u: { id: string }) => u.id);
        setSelectedAvailableIds(new Set(ids));
      }
    } catch {
      toast.error("Failed to select all affiliates");
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handlePageSizeChange = (nextSize: number) => {
    setPageSize(nextSize);
    setAssignedPage(1);
    setAvailablePage(1);
    setApplicantsPage(1);
  };

  const total = currentPagination?.total ?? 0;
  const totalPages = Math.max(currentPagination?.totalPages ?? 1, 1);
  const selectionCount = selectedIds.size;
  const rowNoun = activeTab === "applicants" ? "applicants" : "affiliates";
  const mutationsBusy =
    removeMutation.isPending ||
    bulkRemoveMutation.isPending ||
    bulkAssignMutation.isPending ||
    decideApplicationsMutation.isPending;

  return (
    <section
      className={cn(
        "relative flex flex-col rounded-xl border border-border bg-card shadow-sm",
        className,
      )}
    >
      <div className="shrink-0 px-5 pt-3">
        {isSuspended && (
          <Alert variant="warning" className="mb-3">
            <AlertDescription>
              This program is suspended. Affiliate assignment is disabled.
              Please upgrade your plan to reactivate this program and assign
              affiliates.
            </AlertDescription>
          </Alert>
        )}

        <UnderlineTabs<Tab>
          tabs={
            [
              {
                value: "assigned",
                label: "Assigned Affiliates",
                count: assignedPagination?.total ?? assignedUsers.length,
              },
              ...(showAssignPanel
                ? [
                    {
                      value: "available",
                      label: "Available Affiliates",
                      count: availablePagination?.total ?? otherUsers.length,
                    } as UnderlineTabItem<Tab>,
                  ]
                : []),
              ...(showApplicantsTab
                ? [
                    {
                      value: "applicants",
                      label: (
                        <>
                          Applicants
                          <span
                            className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
                              activeTab === "applicants"
                                ? "bg-brand-100 text-brand-700"
                                : applicantsTotal > 0
                                  ? "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                                  : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {applicantsTotal}
                          </span>
                        </>
                      ),
                    } as UnderlineTabItem<Tab>,
                  ]
                : []),
            ] as UnderlineTabItem<Tab>[]
          }
          value={activeTab}
          onValueChange={setActiveTab}
          trailing={
            <div className="flex items-center gap-2">
              {isFetching && !isLoading && (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  Updating…
                </span>
              )}
              <div className="relative w-64 sm:w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email or code…"
                  className="w-full rounded-lg border border-border pl-9 pr-3 py-1.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                />
              </div>
            </div>
          }
        />
      </div>

      <div className="relative flex-1">
        {(showSelectAllAcrossPages || allAcrossPagesSelected) &&
          totalMatching > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-b border-brand-100 bg-brand-50/70 px-5 py-2 text-sm text-brand-800">
              {allAcrossPagesSelected ? (
                <>
                  <span>
                    All{" "}
                    <span className="font-semibold">{totalMatching}</span>{" "}
                    {rowNoun} are selected
                    {debouncedSearch ? " matching this search" : ""}.
                  </span>
                  <button
                    type="button"
                    className="font-medium text-brand-700 underline hover:text-brand-900"
                    onClick={() => setSelectedIds(new Set())}
                    disabled={mutationsBusy || isSelectingAll}
                  >
                    Clear selection
                  </button>
                </>
              ) : (
                <>
                  <span>
                    All{" "}
                    <span className="font-semibold">
                      {currentPageIds.length}
                    </span>{" "}
                    on this page are selected.
                  </span>
                  <button
                    type="button"
                    className="font-medium text-brand-700 underline hover:text-brand-900 disabled:opacity-50"
                    onClick={handleSelectAllAcrossPages}
                    disabled={
                      isSuspended || mutationsBusy || isSelectingAll
                    }
                  >
                    {isSelectingAll
                      ? "Selecting…"
                      : `Select all ${totalMatching} ${rowNoun}`}
                  </button>
                </>
              )}
            </div>
          )}
        <AffiliateRosterTable
          mode={activeTab}
          rows={currentRows}
          selectedIds={selectedIds}
          allPageSelected={allPageSelected}
          isSuspended={isSuspended}
          isLoading={isLoading}
          pageSize={pageSize}
          addingId={adding}
          disabled={mutationsBusy || isSelectingAll || adding !== null}
          attributionMethod={attributionMethod}
          onToggleId={toggleId}
          onToggleAllPage={handleSelectAllPage}
          onRemove={setRemoveTarget}
          onAssign={handleAdd}
          onApprove={(row) =>
            decideApplicationsMutation.mutate({
              applicationIds: [row.id],
              approve: true,
            })
          }
          onReject={(row) =>
            decideApplicationsMutation.mutate({
              applicationIds: [row.id],
              approve: false,
            })
          }
        />
      </div>

      {selectionCount > 0 && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 px-4">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-200 bg-card px-4 py-2.5 shadow-lg shadow-brand-500/10 ring-1 ring-brand-100">
            <span className="text-sm font-medium text-brand-700">
              {selectionCount} selected
              {allAcrossPagesSelected ? " (all)" : ""}
            </span>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setSelectedIds(new Set())}
              disabled={mutationsBusy}
            >
              Clear
            </button>
            {showSelectAllAcrossPages && (
              <>
                <span className="h-4 w-px bg-border" />
                <button
                  type="button"
                  className="text-xs text-brand-600 hover:text-brand-800 font-medium"
                  onClick={handleSelectAllAcrossPages}
                  disabled={isSuspended || mutationsBusy || isSelectingAll}
                >
                  {isSelectingAll
                    ? "Selecting…"
                    : `Select all ${totalMatching}`}
                </button>
              </>
            )}
            <span className="h-4 w-px bg-border" />
            {activeTab === "applicants" ? (
              <>
                <button
                  type="button"
                  className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                  onClick={() =>
                    decideApplicationsMutation.mutate({
                      applicationIds: Array.from(selectedApplicantIds),
                      approve: true,
                    })
                  }
                  disabled={isSuspended || mutationsBusy}
                >
                  {decideApplicationsMutation.isPending
                    ? "Working…"
                    : `Approve selected (${selectionCount})`}
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                  onClick={() =>
                    decideApplicationsMutation.mutate({
                      applicationIds: Array.from(selectedApplicantIds),
                      approve: false,
                    })
                  }
                  disabled={mutationsBusy}
                >
                  Reject
                </button>
              </>
            ) : activeTab === "assigned" ? (
              <button
                type="button"
                className="rounded-lg bg-red-50 dark:bg-red-950/40 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/60 disabled:opacity-50"
                onClick={() => setBulkRemoveConfirmOpen(true)}
                disabled={isSuspended || mutationsBusy || adding !== null}
              >
                {bulkRemoveMutation.isPending
                  ? "Removing…"
                  : `Remove selected (${selectionCount})`}
              </button>
            ) : (
              <button
                type="button"
                className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                onClick={() =>
                  bulkAssignMutation.mutate(Array.from(selectedAvailableIds))
                }
                disabled={isSuspended || mutationsBusy || adding !== null}
              >
                {bulkAssignMutation.isPending
                  ? "Assigning…"
                  : `Assign selected (${selectionCount})`}
              </button>
            )}
          </div>
        </div>
      )}

      <DataTablePagination
        page={currentPage}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        noun={rowNoun}
      />

      <RemoveAffiliatesModal
        open={removalRequest !== null}
        programId={programId}
        affiliateIds={removalRequest?.ids ?? []}
        subject={removalRequest?.subject ?? ""}
        isRemoving={isRemoving}
        onClose={closeRemovalDialog}
        onConfirm={confirmRemoval}
      />
    </section>
  );
}
